import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PrefsSection } from "@/components/settings/PrefsSection";
import { cardCls, btnPrimary, btnSecondary } from "@/components/shared";
import { PinInput } from "@/components/diary/PinLock";

const PROMPT_ITEMS = [{ k: "diary_prompts", title: "Writing prompts", text: "Show a gentle question to help you get started on new entries." }];

const PinForm = ({ mode, onDone, setToken }) => {
  const { setUser } = useAuth();
  const [f, setF] = useState({ current: "", pin: "", confirm: "" });
  const field = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const needsCurrent = mode !== "on";
  const needsNew = mode !== "off";
  const submit = async (e) => {
    e.preventDefault();
    if (needsNew && f.pin !== f.confirm) return toast.error("PINs don't match");
    try {
      const r = mode === "off"
        ? await api.post("/diary/pin/off", { current_pin: f.current })
        : await api.put("/diary/pin", { pin: f.pin, current_pin: f.current || null });
      setUser(r.data.user);
      if (r.data.token) setToken(r.data.token);
      toast.success(mode === "off" ? "Diary PIN turned off" : "Diary PIN saved");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const row = (label, k) => (
    <label className="block space-y-1.5 text-center">
      <span className="text-xs font-semibold tracking-wide uppercase text-t-muted">{label}</span>
      <PinInput testId={`diary-pin-${k}-input`} value={f[k]} onChange={field(k)} />
    </label>
  );
  return (
    <form onSubmit={submit} className="space-y-4 mt-4" data-testid="diary-pin-form">
      {needsCurrent && row("Current PIN", "current")}
      {needsNew && row("New 4-digit PIN", "pin")}
      {needsNew && row("Confirm PIN", "confirm")}
      <div className="flex gap-2">
        <button type="button" onClick={onDone} className={`${btnSecondary} flex-1`} data-testid="diary-pin-cancel">Cancel</button>
        <button type="submit" className={`${btnPrimary} flex-1`} data-testid="diary-pin-submit">{mode === "off" ? "Turn off" : "Save PIN"}</button>
      </div>
    </form>
  );
};

const PinSection = ({ setToken }) => {
  const { user } = useAuth();
  const [mode, setMode] = useState(null);
  const on = user.diary_pin_enabled;
  return (
    <section className={`${cardCls} p-6`} data-testid="diary-pin-section">
      <h2 className="font-serif-display text-2xl font-semibold">Diary lock</h2>
      <p className="text-sm text-t-muted mt-1" data-testid="diary-pin-status">
        {on ? "PIN lock is on — you'll be asked for it each time you open your diary." : "PIN lock is off."}
      </p>
      {mode ? <PinForm mode={mode} onDone={() => setMode(null)} setToken={setToken} /> : (
        <div className="flex gap-2 mt-4 flex-wrap">
          {on ? (
            <>
              <button className={btnSecondary} onClick={() => setMode("change")} data-testid="diary-pin-change">Change PIN</button>
              <button className={`${btnSecondary} text-t-danger`} onClick={() => setMode("off")} data-testid="diary-pin-turn-off">Turn off PIN</button>
            </>
          ) : <button className={btnPrimary} onClick={() => setMode("on")} data-testid="diary-pin-turn-on">Turn on PIN</button>}
        </div>
      )}
    </section>
  );
};

export const DiarySettings = ({ open, onClose, setToken }) => (
  <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
    <DialogContent aria-describedby={undefined} className="bg-t-bg border-t-line rounded-2xl sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="diary-settings-dialog">
      <DialogHeader><DialogTitle className="font-serif-display text-3xl font-semibold">Diary settings</DialogTitle></DialogHeader>
      <div className="space-y-4">
        <PrefsSection testId="diary-prompts-section" title="Prompts" subtitle="A little nudge when the page feels blank." items={PROMPT_ITEMS} />
        <PinSection setToken={setToken} />
      </div>
    </DialogContent>
  </Dialog>
);
